/**
 * QrCodeService
 * Service for QR code generation and decoding
 * Route: api/codes/qr
 * Controller: QrCodesController.cs
 */
import { BaseService } from '../base/BaseService';
import { ReturnType, } from '../../models/codegenerators';
/**
 * QrCodeService
 * Handles QR code generation, decoding, and customization operations
 */
export class QrCodeService extends BaseService {
    constructor(http, config) {
        super(http, {
            servicePath: config?.servicePath ?? QrCodeService.DEFAULT_SERVICE_PATH,
            basePath: QrCodeService.BASE_PATH,
            endpoints: config?.endpoints,
        });
    }
    // ==================== QR CODE GENERATION ====================
    /**
     * Generate a QR code from text or URL
     * POST api/codes/qr/generate
     */
    async generate(data) {
        const response = await this.generateRaw(data);
        if (!response.data) {
            throw new Error(response.message || 'Failed to generate QR code');
        }
        return response.data;
    }
    /**
     * Generate QR code with full response
     */
    generateRaw(data) {
        const endpoint = this.getEndpoint('generate', 'generate');
        const url = this.buildUrl(endpoint);
        return this.http.post(url, data);
    }
    /**
     * Generate a QR code with a logo overlay
     * POST api/codes/qr/generate/with-logo
     */
    async generateWithLogo(data, logoFile, size = 300, logoSizePercent = 20, returnType = ReturnType.File) {
        const response = await this.generateWithLogoRaw(data, logoFile, size, logoSizePercent, returnType);
        if (!response.data) {
            throw new Error(response.message || 'Failed to generate QR code with logo');
        }
        return response.data;
    }
    /**
     * Generate QR code with logo and full response
     */
    generateWithLogoRaw(data, logoFile, size = 300, logoSizePercent = 20, returnType = ReturnType.File) {
        const endpoint = this.getEndpoint('generate/with-logo', 'generate/with-logo');
        const url = this.buildUrl(endpoint);
        const formData = new FormData();
        formData.append('data', data);
        formData.append('logo', logoFile);
        formData.append('size', size.toString());
        formData.append('logoSizePercent', logoSizePercent.toString());
        formData.append('returnType', returnType);
        return this.http.post(url, formData);
    }
    /**
     * Generate a QR code with a frame/border and text
     * POST api/codes/qr/generate/with-frame
     */
    async generateWithFrame(data, frameText, size = 300, returnType = ReturnType.File) {
        const response = await this.generateWithFrameRaw(data, frameText, size, returnType);
        if (!response.data) {
            throw new Error(response.message || 'Failed to generate QR code with frame');
        }
        return response.data;
    }
    /**
     * Generate QR code with frame and full response
     */
    generateWithFrameRaw(data, frameText, size = 300, returnType = ReturnType.File) {
        const endpoint = this.getEndpoint('generate/with-frame', 'generate/with-frame');
        const url = this.buildUrl(endpoint);
        const formData = new FormData();
        formData.append('data', data);
        formData.append('frameText', frameText);
        formData.append('size', size.toString());
        formData.append('returnType', returnType);
        return this.http.post(url, formData);
    }
    // ==================== DECODING ====================
    /**
     * Decode a QR code from an uploaded image
     * POST api/codes/qr/decode
     */
    async decode(imageFile) {
        const response = await this.decodeRaw(imageFile);
        if (!response.data) {
            throw new Error(response.message || 'Failed to decode QR code');
        }
        return response.data;
    }
    /**
     * Decode QR code with full response
     */
    decodeRaw(imageFile) {
        const endpoint = this.getEndpoint('decode', 'decode');
        const url = this.buildUrl(endpoint);
        const formData = new FormData();
        formData.append('image', imageFile);
        return this.http.post(url, formData);
    }
}
QrCodeService.DEFAULT_SERVICE_PATH = 'codegenerators';
QrCodeService.BASE_PATH = 'api/codes/qr';
//# sourceMappingURL=QrCodeService.js.map